import React from "react";
import type { LoadPhase } from "./useLoadPhase";

/** The pale fill a placeholder is drawn in, under the band that crosses it. */
const BLOCK = "bg-[#ECF0F3] skeleton-sheen";

export interface SkeletonProps {
  /** Size and shape of this block — a height, a width, and any rounding. */
  className?: string;
  /**
   * For the one size a class cannot carry: a measurement worked out at render
   * time.
   */
  style?: React.CSSProperties;
}

/**
 * One block of a loading screen, with a band of light crossing it.
 *
 * A block stands in for a whole card, or for a whole region of one — never for
 * the lines and pills and avatars inside it. A screen that draws every one of
 * those is a screen of forty blocks each running a band of its own, at forty
 * different speeds, and it reads as forty things loading rather than as a page
 * on its way.
 *
 * `SkeletonCard` is what screens reach for; this is the block underneath it,
 * for the odd placeholder that is not a card and has no phase to answer to.
 *
 * Hidden from screen readers, always. A skeleton is the shape of an answer
 * rather than the answer, and a screenful of them read out is worse than
 * silence: the screen that places them carries one `role="status"` line saying
 * what is on its way.
 */
export const Skeleton: React.FC<SkeletonProps> = ({ className = "", style }) => (
  <span
    aria-hidden="true"
    style={style}
    className={`block rounded ${BLOCK} ${className}`}
  />
);

export interface SkeletonCardProps {
  /**
   * How long the card's contents have been coming. Only `slow` draws the
   * block; while it is merely `waiting` the card's space is held open and left
   * blank, so a screen that answers quickly goes from empty to filled without a
   * shimmer in between.
   */
  phase?: LoadPhase;
  /**
   * The card's own footprint — its height, and its rounding where that is not
   * the usual `rounded-md`. What lands in this block's place should be the
   * same size and in the same spot, so the page does not jump as it fills in.
   */
  className?: string;
  style?: React.CSSProperties;
}

/**
 * A whole card, standing in for a card that has not arrived.
 *
 * One block per card is the whole of it: seven cards on a screen shimmer as
 * seven cards, not as the hundred lines, figures and pills that will be inside
 * them. What each card turns out to hold is the card's business once it lands,
 * and drawing it twice — once in blocks and once for real — is what makes a
 * loading screen look busier than the screen it stands for.
 */
export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  phase = "slow",
  className = "",
  style,
}) =>
  phase === "slow" ? (
    <Skeleton className={`rounded-md ${className}`} style={style} />
  ) : (
    // The card's space, held open and empty: the block appears in it without
    // anything around it moving.
    <div aria-hidden="true" className={className} style={style} />
  );

/**
 * What a table's body shimmers as while its rows are fetched: one block filling
 * the card's body, rather than a page of blocks in the shape of the cells.
 *
 * The card is what is waiting; how many rows it turns out to hold, and what is
 * in them, is its business once they land. Every list screen in the app uses
 * this, so they all wait alike.
 */
export const TABLE_BODY_SKELETON = "h-full min-h-[240px]";

/**
 * `count` placeholders to map over — `[0, 1, 2, …]`, since an index is the
 * only key a block standing in for nothing can be given.
 */
export const skeletonRows = (count: number): number[] =>
  Array.from({ length: count }, (_, index) => index);

export default Skeleton;
